import SDKConfiguraitonModel from "../Configurator/Models/SDKConfiguraitonModel";
import {
  initDisableCache,
  isDisabledCache,
  setBaseUrl,
} from "../Global/Helper";
import BracketGameFilters from "../Namespaces/BracketGame/Models/Filters/BracketGameFilters";
import BracketGamePredictionsFilters from "../Namespaces/BracketGame/Models/Filters/BracketGamePredictionsFilters";
import BracketGameRankingsFilters from "../Namespaces/BracketGame/Models/Filters/BracketGameRankingsFilters";
import BracketGamePredictionRequestBody from "../Namespaces/BracketGame/Models/Prediction/RequestBody/BracketGamePredictionRequestBody";
import BracketGameRemapper from "../Namespaces/BracketGame/Models/Remapper/BracketGameRemapper";
import StandingGamesFilters from "../Namespaces/StandingGame/Models/Filters/StandingGamesFilters";
import StandingGameRankingsFilters from "../Namespaces/StandingGame/Models/Filters/StandingGameRankingsFilters";
import StandingGameRemapper from "../Namespaces/StandingGame/Models/Remapper/StandingGameRemapper";
import Https from "./Https";
import EventGameFilters from "../Namespaces/EventGame/Models/Filters/EventGameFilters";
import EventGameRemapper from "../Namespaces/EventGame/Models/Remapper/EventGameRemapper";
import EventGamePredictionRequestBody from "../Namespaces/EventGame/Models/Prediction/RequestBody/EventGamePredictionRequestBody";
import EventGamePredictionsFilters from "../Namespaces/EventGame/Models/Filters/EventGamePredictionsFilters";
import EventGameRankingsFilters from "../Namespaces/EventGame/Models/Filters/EventGameRankingsFilters";

const bracketGamePrediction = {
  id: "",
  profile_id: "",
  total_points: null,
  game_points: null,
  bonus_points: null,
  position: null,
  status: "ACTIVE",
  game_id: "",
  fixtures: [],
  tiebreaker: { stat_total: 0 },
  updated_at: "",
  created_at: "",
};

export default class CustomGameHttps extends Https {
  private bracketGameRemapper: BracketGameRemapper = null;
  private standingGameRemapper: StandingGameRemapper = null;
  private eventGameRemapper: EventGameRemapper = null;

  constructor(config: SDKConfiguraitonModel) {
    super(config, setBaseUrl(config.environment, "predictor"));
    this.bracketGameRemapper = new BracketGameRemapper();
    this.standingGameRemapper = new StandingGameRemapper();
    this.eventGameRemapper = new EventGameRemapper();
  }

  public getBracketGames = async (
    filters: BracketGameFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/bracket${
          this.apiSignInUrl
        }${filters.constructMainFiltersUrl()}`
      : `/custom/bracket${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = "There was a problem with fetching bracket games list";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.bracketGameRemapper.remapBracketGames(data);
  };

  public getBracketGameById = async (gameId: string, disableCache: boolean) => {
    const url = isDisabledCache(disableCache)
      ? `/custom/bracket/${gameId}${this.apiSignInUrl}${initDisableCache()}`
      : `/custom/bracket/${gameId}${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with fetching bracket game with the following ID: " +
      gameId;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.bracketGameRemapper.remapBracketGameById(data);
  };

  public makeBracketGamePrediction = async (
    gameId: string,
    requestBody: BracketGamePredictionRequestBody
  ) => {
    const url = `/custom/bracket/${gameId}/predictions${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with making prediction for bracket game with the following ID: " +
      gameId;
    const method = "POST";
    const copyRequestBody = JSON.parse(JSON.stringify(requestBody));
    copyRequestBody.fixtures =
      this.bracketGameRemapper.remapPredictionToRequestBody(
        requestBody.fixtures
      );

    if (requestBody.tiebreaker) {
      copyRequestBody.tiebreaker = {
        stat_total: requestBody.tiebreaker.statTotal,
      };
    }

    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      copyRequestBody
    );

    return this.bracketGameRemapper.remapPrediction(data);
  };

  public updateBracketGamePrediction = async (
    gameId: string,
    predictionId: string,
    requestBody: BracketGamePredictionRequestBody
  ) => {
    const url = `/custom/bracket/${gameId}/predictions/${predictionId}${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with updating prediction for bracket game with the following ID: " +
      gameId;
    const method = "PUT";
    const copyRequestBody = JSON.parse(JSON.stringify(requestBody));
    copyRequestBody.fixtures =
      this.bracketGameRemapper.remapPredictionToRequestBody(
        requestBody.fixtures
      );

    if (requestBody.tiebreaker) {
      copyRequestBody.tiebreaker = {
        stat_total: requestBody.tiebreaker.statTotal,
      };
    }

    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      copyRequestBody
    );

    return this.bracketGameRemapper.remapPrediction(data);
  };

  public getBracketGamePredictionsForUser = async (
    gameId: string,
    profileId: string,
    filters: BracketGamePredictionsFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/bracket/${gameId}/predictions${
          this.apiSignInUrl
        }&profile_id=${profileId}${filters.constructMainFiltersUrl()}`
      : `/custom/bracket/${gameId}/predictions${this.apiSignInUrl}&profile_id=${profileId}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = `There was a problem with getting all predictions for user with ID ${profileId} for bracket game with ID ${gameId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.bracketGameRemapper.remapPredictionsForUser(data);
  };

  public getBracketGamePredictionById = async (
    gameId: string,
    predictionId: string,
    disableCache: boolean
  ) => {
    const url = isDisabledCache(disableCache)
      ? `/custom/bracket/${gameId}/predictions/${predictionId}/${
          this.apiSignInUrl
        }${initDisableCache()}`
      : `/custom/bracket/${gameId}/predictions/${predictionId}/${this.apiSignInUrl}`;
    const warnMessage = `There was a problem with getting prediction with ID ${predictionId} for bracket game with ID ${gameId}`;
    //TODO: Call API when functionality is ready
    // const data = await this.fetchWithoutAuth(url, warnMessage);
    const copyBracketGamePrediction = JSON.parse(
      JSON.stringify(bracketGamePrediction)
    );
    copyBracketGamePrediction.id = predictionId;
    copyBracketGamePrediction.game_id = gameId;
    copyBracketGamePrediction.tiebreaker = null;

    return Promise.resolve(
      this.bracketGameRemapper.remapPrediction(copyBracketGamePrediction)
    );
  };

  public getBracketGameRankings = async (
    gameId: string,
    filters: BracketGameRankingsFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/bracket/${gameId}/ranking${
          this.apiSignInUrl
        }${filters.constructMainFiltersUrl()}`
      : `/custom/bracket/${gameId}/ranking${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = `There was a problem with getting rankings for bracket game with ID ${gameId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.bracketGameRemapper.remapRankings(data);
  };

  public getStandingsGames = async (
    filters: StandingGamesFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/standing${
          this.apiSignInUrl
        }${filters.constructMainFiltersUrl()}`
      : `/custom/standing${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage =
      "There was a problem with fetching standings games list";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.standingGameRemapper.remapBracketGames(data);
  };

  public getStandingGameById = async (
    gameId: string,
    disableCache: boolean
  ) => {
    const url = isDisabledCache(disableCache)
      ? `/custom/standing/${gameId}${this.apiSignInUrl}${initDisableCache()}`
      : `/custom/standing/${gameId}${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with fetching standing game with the following ID: " +
      gameId;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.standingGameRemapper.remapStandingGameById(data);
  };

  public makeStandingGamePrediction = async (
    gameId: string,
    standing: string[]
  ) => {
    const url = `/custom/standing/${gameId}/predictions${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with making prediction for standing game with the following ID: " +
      gameId;
    const method = "POST";
    const requestBody = { standing };

    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      requestBody
    );

    return this.standingGameRemapper.remapPrediction(data);
  };

  public updateStandingGamePrediction = async (
    gameId: string,
    predictionId: string,
    standing: string[]
  ) => {
    const url = `/custom/standing/${gameId}/predictions/${predictionId}${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with updating prediction for standing game with the following ID: " +
      gameId;
    const method = "PUT";
    const requestBody = { standing };

    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      requestBody
    );

    return this.standingGameRemapper.remapPrediction(data);
  };

  public getStandingGamePredictionsForUser = async (
    gameId: string,
    profileId: string,
    disableCache: boolean
  ) => {
    let url = `/custom/standing/${gameId}/predictions${this.apiSignInUrl}&profile_id=${profileId}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = `There was a problem with getting prediction for user with ID ${profileId} for standing game with ID ${gameId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.standingGameRemapper.remapPredictionsForUser(data);
  };

  public getStandingGameRankings = async (
    gameId: string,
    filters: StandingGameRankingsFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/standing/${gameId}/ranking${
          this.apiSignInUrl
        }${filters.constructMainFiltersUrl()}`
      : `/custom/standing/${gameId}/ranking${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = `There was a problem with getting rankings for standing game with ID ${gameId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.standingGameRemapper.remapRankings(data);
  };

  public getEventGames = async (
    filters: EventGameFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/event${this.apiSignInUrl}${filters.constructMainFiltersUrl()}`
      : `/custom/event${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = "There was a problem with fetching event games list";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eventGameRemapper.remapEventGames(data);
  };

  public getEventGameById = async (gameId: string, disableCache: boolean) => {
    const url = isDisabledCache(disableCache)
      ? `/custom/event/${gameId}${this.apiSignInUrl}${initDisableCache()}`
      : `/custom/event/${gameId}${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with fetching event game with the following ID: " +
      gameId;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eventGameRemapper.remapEventGameById(data);
  };

  public makeEventGamePrediction = async (
    gameId: string,
    requestBody: EventGamePredictionRequestBody
  ) => {
    const url = `/custom/event/${gameId}/predictions${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with making prediction for event game with the following ID: " +
      gameId;
    const method = "POST";
    const copyRequestBody = JSON.parse(JSON.stringify(requestBody));
    copyRequestBody.fixtures =
      this.eventGameRemapper.remapPredictionToRequestBody(requestBody.fixtures);

    if (requestBody.tiebreaker) {
      copyRequestBody.tiebreaker = {
        stat_total: requestBody.tiebreaker.statTotal,
      };
    }

    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      copyRequestBody
    );

    return this.eventGameRemapper.remapPrediction(data);
  };

  public updateEventGamePrediction = async (
    gameId: string,
    predictionId: string,
    requestBody: EventGamePredictionRequestBody
  ) => {
    const url = `/custom/event/${gameId}/predictions/${predictionId}${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with updating prediction for event game with the following ID: " +
      gameId;
    const method = "PUT";
    const copyRequestBody = JSON.parse(JSON.stringify(requestBody));
    copyRequestBody.fixtures =
      this.eventGameRemapper.remapPredictionToRequestBody(requestBody.fixtures);

    if (requestBody.tiebreaker) {
      copyRequestBody.tiebreaker = {
        stat_total: requestBody.tiebreaker.statTotal,
      };
    }

    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      copyRequestBody
    );

    return this.eventGameRemapper.remapPrediction(data);
  };

  public getEventGamePredictionsForUser = async (
    gameId: string,
    profileId: string,
    filters: EventGamePredictionsFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/event/${gameId}/predictions${
          this.apiSignInUrl
        }&profile_id=${profileId}${filters.constructMainFiltersUrl()}`
      : `/custom/event/${gameId}/predictions${this.apiSignInUrl}&profile_id=${profileId}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = `There was a problem with getting all predictions for user with ID ${profileId} for event game with ID ${gameId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eventGameRemapper.remapPredictionsForUser(data);
  };

  public getEventGameRankings = async (
    gameId: string,
    filters: EventGameRankingsFilters,
    disableCache: boolean
  ) => {
    let url = filters
      ? `/custom/event/${gameId}/ranking${
          this.apiSignInUrl
        }${filters.constructMainFiltersUrl()}`
      : `/custom/event/${gameId}/ranking${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = `There was a problem with getting rankings for event game with ID ${gameId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eventGameRemapper.remapRankings(data);
  };
}
